import { useTrans } from '@/shared/hooks/useTranslation';
import { ProcessingBlock, TimelineNode } from '@/view/shared/Timeline';
import {
  type ActivityEventView,
  useActivityTimelineViewModel,
} from '@/viewmodel/features/observability';
import { AlertCircle, Check, Clock } from 'lucide-react';

interface ActivityTimelinePanelProps {
  readonly correlationId?: string | undefined;
  readonly workspaceId?: string | undefined;
  readonly defaultOpen?: boolean | undefined;
}

function EventIcon({ event }: { readonly event: ActivityEventView }) {
  if (event.errored) return <AlertCircle size={12} />;
  return event.completed ? <Check size={12} /> : <Clock size={12} />;
}

export function ActivityTimelinePanel({
  correlationId,
  workspaceId,
  defaultOpen = false,
}: ActivityTimelinePanelProps) {
  const { t } = useTrans();
  const { visible, events, isLoading, isError, isTerminal, statusKey } =
    useActivityTimelineViewModel(correlationId, workspaceId);

  if (!visible) return null;

  return (
    <div className="pl-9">
      <ProcessingBlock
        summary={t('observability.activity.title')}
        isProcessing={!isTerminal && !isError}
        statusText={t(`observability.activity.${statusKey}`)}
        defaultOpen={defaultOpen}
      >
        {isError ? (
          <p className="text-body-xs text-danger leading-relaxed">
            {t('observability.activity.error')}
          </p>
        ) : events.length === 0 ? (
          <p className="text-body-xs text-muted leading-relaxed">
            {isLoading ? t('observability.activity.loading') : t('observability.activity.empty')}
          </p>
        ) : (
          <div className="flex flex-col gap-2 border-l border-border ml-3 pl-6">
            {events.map((e) => (
              <TimelineNode
                key={e.id}
                error={e.errored}
                accent={e.completed}
                icon={<EventIcon event={e} />}
              >
                <p
                  className={
                    e.errored
                      ? 'text-body-xs text-danger leading-relaxed'
                      : 'text-body-xs text-foreground leading-relaxed'
                  }
                >
                  {e.text}
                </p>
                {e.status && <p className="text-body-2xs text-muted">{e.status}</p>}
              </TimelineNode>
            ))}
          </div>
        )}
      </ProcessingBlock>
    </div>
  );
}
